import React, { useState } from 'react';
import { Button } from '../components/Button';
import { Smartphone, CheckCircle2, Shield, Zap, ArrowLeft, Mail, User, Globe, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

export const WaitingList: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dob: '',
    phone: '',
    country: 'Zimbabwe'
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const webhookUrl = 'https://hook.eu1.make.com/n2th8s8t1ew301tdfyrfnssanyk3gr0m';

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          dateOfBirth: formData.dob, // Explicitly included for clarity, though accessible via spread
          source: 'MHC Africa Website Waiting List',
          timestamp: new Date().toISOString()
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error('Failed to submit. Please try again later.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-32 pb-24 min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-8 animate-fade-in-up">
          <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto shadow-xl shadow-green-500/10">
            <CheckCircle2 size={48} />
          </div>
          <div className="space-y-4">
            <h1 className="text-3xl font-extrabold text-secondary">You're on the list!</h1>
            <p className="text-gray-500 leading-relaxed">
              Thank you for joining the MHC Africa waiting list. We'll notify you as soon as the app is ready for download in your region.
            </p>
          </div>
          <div className="pt-4">
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft size={18} /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Hero Section - White Background */}
      <div className="bg-white text-secondary py-20 px-6 relative overflow-hidden flex flex-col items-center justify-center min-h-[calc(100vh-80px)]">
        {/* Subtle decorative blurs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2"></div>
        
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-primary border border-blue-100 text-xs font-bold uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                Coming Soon
              </div>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-tight text-secondary">
                Be the First to <br/>
                <span className="text-primary">Experience MHC</span>
              </h1>
              <p className="text-xl text-gray-500 leading-relaxed max-w-xl mx-auto lg:mx-0 font-medium">
                Join our exclusive waiting list to get early access, priority booking, and special founding member rewards when we launch.
              </p>
              
              <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-primary border border-gray-100">
                    <Shield size={20} />
                  </div>
                  <span className="text-sm font-bold text-gray-600">Privacy Guaranteed</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-blue-500 border border-gray-100">
                    <Zap size={20} />
                  </div>
                  <span className="text-sm font-bold text-gray-600">Priority Access</span>
                </div>
              </div>
            </div>

            {/* Waiting List Form */}
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-2xl shadow-gray-200/50 border border-gray-100 text-secondary">
              <h2 className="text-2xl font-bold mb-6">Join the Waiting List</h2>
              
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm font-medium">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-2 text-left">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <User size={14} className="text-primary" /> First Name
                    </label>
                    <input 
                      required 
                      type="text" 
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" 
                      placeholder="First name" 
                    />
                  </div>
                  <div className="space-y-2 text-left">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <User size={14} className="text-primary" /> Last Name
                    </label>
                    <input 
                      required 
                      type="text" 
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" 
                      placeholder="Last name" 
                    />
                  </div>
                </div>
                
                <div className="space-y-2 text-left">
                  <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                    <Mail size={14} className="text-primary" /> Email Address
                  </label>
                  <input 
                    required 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" 
                    placeholder="name@example.com" 
                  />
                </div>

                <div className="space-y-2 text-left">
                  <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                    <Calendar size={14} className="text-primary" /> Date of Birth
                  </label>
                  <input 
                    required 
                    type="date" 
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-gray-600 font-medium placeholder-gray-400" 
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-2 text-left">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <Smartphone size={14} className="text-primary" /> Phone Number
                    </label>
                    <input 
                      required 
                      type="tel" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all" 
                      placeholder="+263..." 
                    />
                  </div>
                  <div className="space-y-2 text-left">
                    <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <Globe size={14} className="text-primary" /> Country
                    </label>
                    <select 
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all appearance-none font-medium text-gray-600"
                    >
                      <option>Zimbabwe</option>
                      <option>South Africa</option>
                      <option>Kenya</option>
                      <option>Nigeria</option>
                      <option>Ghana</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full py-4 text-lg shadow-xl shadow-primary/20 gap-3"
                  >
                    {loading ? (
                      <span className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    ) : (
                      <>
                        <Smartphone size={20} strokeWidth={2.5} />
                        Join Now
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-[10px] text-gray-400 text-center mt-4 uppercase font-bold tracking-widest">
                  100% Private • No Spam • Opt-out anytime
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
